import React from 'react';
import { Box, IconButton, SortDirection, TablePagination } from '@mui/material';
import TableHead from './TableHead';
import { Identifier, RecordType } from 'components/record/RecordContext';
import { useFetch } from 'hooks/useFetchData';
import { useResourceContext } from 'components/record/ResourceContext';
import { ListContextProvider } from './ListContext';
import { Sync } from '@mui/icons-material';
import TableToolbar from './actionMenu/TableToolbar';
import BulkDelete from 'components/forms/BulkDelete';
import { Loader, Error } from 'components/common/General';
import ListView from './ListView';

const List = <T extends RecordType>(props: ListProps<T>) => {
  const {
    children,
    bulkOptions = false,
    hidePagination = false,
    filters = {},
    title,
    refresh = false,
    avatar,
    onChangeSelected,
    bulkActions = <BulkDelete />,
    toolbar,
    listFilters,
    identifier = 'id',
    isOrderable = false,
    columnVisibility = false,
    size
  } = props;

  const { resource, params } = useResourceContext();

  const [page, setPage] = React.useState(0);
  const [order, setOrder] = React.useState<SortDirection>('asc');
  const [orderBy, setOrderBy] = React.useState('id');
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [actionFilters, setActionFilters] = React.useState({});

  const variables = React.useMemo(
    () => ({
      ...params,
      filters: {
        ...filters,
        ...actionFilters
      },
      // orderBy, //TODO:
      // order: order === 'asc' ? 'asc' : 'desc',
      ...(!hidePagination ? { page: page + 1, size: rowsPerPage } : size ? { size } : {})
    }),
    [page, rowsPerPage, orderBy, order, hidePagination, params, filters, actionFilters]
  );

  const queryResult = useFetch<{ items: T[]; total: { count: number } }>('GET_LIST', resource, {
    variables
  });
  const { error, data, loading, refetch } = queryResult;
  // Keep last result while variables change so filters/UI stay mounted instead of flashing Loader
  const previousData = 'previousData' in queryResult ? queryResult.previousData : undefined;
  const resolvedData = data ?? previousData;

  // Add local state for the list
  const list = React.useMemo(
    () => (resolvedData?.items || []).map((item: T) => ({ ...item, id: item?.[identifier] ?? Math.random() })),
    [resolvedData, identifier]
  );

  const addFilters = React.useCallback((filters: ListFiltersType) => {
    setPage(0);
    setActionFilters((actionFilters) => {
      const next = { ...actionFilters, ...filters };
      Object.keys(next).forEach((key) => {
        const value = next[key];
        if (value === null || value === undefined || value === '' || value === 'all') {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  const count = React.useMemo(
    () => resolvedData?.total?.count || resolvedData?.items?.length || 0,
    [resolvedData]
  );

  const handleSort = (id: string) => {
    const isAsc = orderBy === id && order === 'asc';
    if (id !== '') {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(id);
    }
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  if (loading && !resolvedData) {
    return <Loader />;
  }
  if (error && !resolvedData) {
    return <Error message={typeof error === 'string' ? error : error.message} />;
  }

  return (
    <ListContextProvider refetch={refetch} filters={actionFilters} addFilters={addFilters} data={{ list, total: count }} isOrderable={isOrderable}>
      <ListView
        headerAvatar={avatar}
        title={title}
        headerAction={
          <Box display="flex" alignItems="center">
            {refresh && (
              <IconButton onClick={() => refetch()} color="primary">
                <Sync />
              </IconButton>
            )}
          </Box>
        }
        columnVisibility={columnVisibility}
        toolbar={toolbar}
        listFilters={listFilters}
        tableToolbar={<TableToolbar onChangeSelected={onChangeSelected} bulkActions={bulkActions} />}
        bulkOptions={bulkOptions}
        tableHead={React.createElement(
          TableHead,
          {
            bulkOptions,
            order,
            orderBy,
            rowCount: count,
            onRequestSort: handleSort,
            ids: list.map((item) => item.id)
          },
          children
        )}
        tablePagination={
          !hidePagination && (
            <TablePagination
              page={page}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              rowsPerPageOptions={[25, 50, 100]}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                width: '100%',
                overflowX: 'auto',
                '& .MuiToolbar-root': {
                  flexWrap: 'wrap',
                  minHeight: { xs: 52, sm: 56 },
                  px: { xs: 0.5, sm: 2 }
                }
              }}
            />
          )
        }
      >
        {children}
      </ListView>
    </ListContextProvider>
  );
};

export default List;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ListFiltersType = any;

export type ListProps<T extends RecordType> = {
  toolbar?: React.ReactElement;
  avatar?: React.ReactNode;
  title?: React.ReactNode;
  refresh?: boolean;
  children: JSX.Element | JSX.Element[];
  filters?: ListFiltersType;
  listFilters?: React.ReactNode;
  list?: T[];
  onEditClick?: (record: T) => void;
  onDeleteClick?: (record: T) => void;
  hidePagination?: boolean;
  onChangeSelected?: (selected: Identifier[]) => void;
  bulkOptions?: boolean;
  bulkActions?: React.ReactNode;
  identifier?: string;
  isOrderable?: boolean;
  columnVisibility?: boolean;
  size?: number;
};
